import localForage from 'localforage';
import { create } from 'zustand';
import {
  createJSONStorage,
  devtools,
  persist,
  type StateStorage,
} from 'zustand/middleware';

import { MetadataKey } from '@revelio/data-access';

import type { AuthStoreRootProps } from './auth.model';

const rlUserAuthDB = localForage.createInstance({
  driver: localForage.INDEXEDDB,
  name: 'rl-dashboard',
  version: 1.0,
  storeName: 'rl_user_auth',
});

export type AuthStoreState = AuthStoreRootProps & {
  isAuthenticated: boolean;
};

export type AuthStoreActions = {
  setOry: (ory: AuthStoreRootProps['ory']) => void;
  setDashMetaCsrfToken: (token: string) => void;
  setUser: (user: AuthStoreRootProps['user']) => void;
  setDataSets: (dataSets: AuthStoreRootProps['dataSets']) => void;
  setIsAuthenticated: (isAuthenticated: boolean) => void;
  reset: () => void;
};

export type AuthStore = AuthStoreState & AuthStoreActions;

const DEFAULT_AUTH_STATE: AuthStoreState = {
  dashMetaCsrfToken: undefined,
  dataSets: undefined,
  user: undefined,
  ory: undefined,
  isAuthenticated: false,
};

const localForageStorage: StateStorage = {
  getItem: async (name) => {
    const value = await rlUserAuthDB.getItem<string | null>(name);
    return value ?? null;
  },
  setItem: async (name, value) => {
    await rlUserAuthDB.setItem(name, value);
  },
  removeItem: async (name) => {
    await rlUserAuthDB.removeItem(name);
  },
};

const mergePersistedState = (
  persistedState: unknown,
  currentState: AuthStore
): AuthStore => {
  const persisted = persistedState as Partial<AuthStoreState>;
  return {
    ...currentState,
    ...persisted,
  };
};

export const useAuthStore = create<AuthStore>()(
  devtools(
    persist(
      (set) => ({
        ...DEFAULT_AUTH_STATE,
        setOry: (ory) => set({ ory }),
        setDashMetaCsrfToken: (token) => set({ dashMetaCsrfToken: token }),
        setUser: (user) => set({ user }),
        setDataSets: (dataSets) => set({ dataSets }),
        setIsAuthenticated: (isAuthenticated) => set({ isAuthenticated }),
        reset: () => set({ ...DEFAULT_AUTH_STATE }),
      }),
      {
        name: 'rl_user_auth',
        storage: createJSONStorage(() => localForageStorage),
        partialize: (state) => ({
          dashMetaCsrfToken: state.dashMetaCsrfToken,
          dataSets: state.dataSets,
        }),
        merge: mergePersistedState,
      }
    ),
    { name: 'AuthStore' }
  )
);

export { MetadataKey };

export function getAuthStoreUser() {
  return useAuthStore.getState().user;
}

export function getAuthDashMetaCsrfToken() {
  return useAuthStore.getState().dashMetaCsrfToken;
}

export function resetAuthStore() {
  useAuthStore.getState().reset();
}
